<template>
    <v-card>
        <v-card-title>
            {{$t('event_restaurant.items')}}
            <v-spacer></v-spacer>
            <v-btn v-if="isEditOrderMode" color="red" dark class="ml-3" @click="cancelEditOrder">
                {{$t('Cancel')}}
                <v-icon right>mdi-close-circle-outline</v-icon>
            </v-btn>
            <v-btn v-if="isEditOrderMode" color="green" dark class="ml-3" @click="saveEditOrder">
                {{$t('Save')}}
                <v-icon right>mdi-check</v-icon>
            </v-btn>
            <v-btn v-if="!isEditOrderMode" color="primary" @click="initEditOrder">
                {{$t('Reorder')}}
                <v-icon right>mdi-swap-vertical</v-icon>
            </v-btn>
            <div class="button-spacer"></div>
            <v-btn v-if="!isEditOrderMode" color="primary" @click="initAddRestaurantItem">
                {{$t('add')}}
                <v-icon right>mdi-plus</v-icon>
            </v-btn>
        </v-card-title>
        <v-data-table :headers="headers"
                      :items="tableRestaurants"
                      :loading="isRestaurantItemPending"
                      @pagination="currentPagination"
                      disable-sort>
            <template v-slot:item.scroller="{value}">
                <div v-show="isEditOrderMode" class="handle"><v-btn icon ><v-icon>drag_handle{{value}}</v-icon></v-btn></div>
                <div v-show="!isEditOrderMode"></div>
            </template>
            <template v-slot:item.restaurant_item_key="{value,item}">
                <v-menu
                    v-if="!isEditOrderMode"
                    :ref="item.id.concat('restaurant_item_key')"
                    :close-on-click="false"
                    :close-on-content-click="false"
                    :nudge-right="10"
                    transition="scale-transition"
                    >
                    <template v-slot:activator="{ on }">
                        <div @click="openInline(item)" v-on="on" style="cursor: pointer;">{{value}}</div>
                    </template>
                    <v-card>
                        <v-card-text>
                            <v-text-field outlined hide-details
                                    :label="$t('restaurant_item.restaurant_item_key')"
                                    :placeholder="$t('restaurant_item.restaurant_item_key')"
                                    v-model="inlineEditingRestaurantItemList[item.id].restaurant_item_key"></v-text-field>
                        </v-card-text>
                        <v-divider></v-divider>
                        <v-card-actions>
                            <v-btn color="secondary" @click="cancelInline(item, item.id.concat('restaurant_item_key'))">{{$t('cancel')}}</v-btn>
                            <v-spacer></v-spacer>
                            <v-btn color="primary" :loading="inlineSaving" @click="saveInlineItem(item, item.id.concat('restaurant_item_key'))">{{$t('save')}}
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-menu>
                <div v-if="isEditOrderMode">{{value}}</div>
            </template>
            <template v-slot:item.html_desc="{value,item}">
                <v-menu
                    v-if="!isEditOrderMode"
                    :ref="item.id.concat('html_desc')"
                    :close-on-click="false"
                    :close-on-content-click="false"
                    :nudge-right="10"
                    transition="scale-transition"
                    >
                    <template v-slot:activator="{ on }">
                        <div @click="openInline(item)" v-on="on" style="cursor: pointer;">
                            <froalaView v-model="value" class="popup-preview"></froalaView>
                        </div>
                    </template>
                    <v-card>
                        <v-card-title>
                            {{$t('restaurant_item.html_desc')}}
                        </v-card-title>
                        <v-card-text>
                            <v-container class="popup-editor-container">
                                <froala
                                    v-if="showEditor"
                                    :config="froalaConfig"
                                    v-model="inlineEditingRestaurantItemList[item.id].html_desc"
                                ></froala>
                            </v-container>
                        </v-card-text>
                        <v-divider></v-divider>
                        <v-card-actions>
                            <v-btn color="secondary" @click="cancelInline(item, item.id.concat('html_desc'))">{{$t('cancel')}}</v-btn>
                            <v-spacer></v-spacer>
                            <v-btn color="primary" :loading="inlineSaving" @click="saveInlineItem(item, item.id.concat('html_desc'))">{{$t('save')}}
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-menu>
                <div v-if="isEditOrderMode">
                    <froalaView v-model="value" class="popup-preview"></froalaView>
                </div>
            </template>
            <template v-slot:item.createdAt="{value}">
                {{DateTime.fromISO(value).toFormat('yyyy-MM-dd HH:mm')}}
            </template>
            <template v-slot:item.updatedAt="{value}">
                {{DateTime.fromISO(value).toFormat('yyyy-MM-dd HH:mm')}}
            </template>
            <template v-slot:item.actions="{item}">
                <v-btn color="primary" class="ma-2" :disabled="isEditOrderMode"
                       @click="initEditRestaurantItem(item)">
                    {{$t('edit')}}
                    <v-icon right>mdi-pencil</v-icon>
                </v-btn>
                <v-btn color="red" :dark="!isEditOrderMode" class="ma-2" :disabled="isEditOrderMode"
                       @click="deleteRestaurantItem(item)">
                    {{$t('delete')}}
                    <v-icon right>mdi-delete</v-icon>
                </v-btn>
            </template>
        </v-data-table>

        <v-dialog v-model="editRestaurantItemDialogShown"
                  max-width="600px"
                  persistent
                  scrollable
                  :retain-focus="false"
                  >
            <v-card>
                <v-card-title>
                    {{$t('restaurant_item.title')}}
                </v-card-title>
                <v-card-text>
                    <v-container>
                        <v-form v-model="isEditRestaurantItemFormValid">
                            <v-row>
                                <v-col class="py-0">
                                    <v-text-field outlined
                                                  :label="$t('restaurant_item.order')"
                                                  :rules=[restaurantOrderRule]
                                                  :placeholder="$t('restaurant_item.order')"
                                                  v-model="editingRestaurantItem.order"></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="py-0">
                                    <v-text-field outlined
                                                  :label="$t('restaurant_item.restaurant_item_key')"
                                                  :placeholder="$t('restaurant_item.restaurant_item_key')"
                                                  v-model="editingRestaurantItem.restaurant_item_key"></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="py-0">
                                    <v-text-field outlined
                                                  :label="$t('restaurant_item.restaurant_item_name')"
                                                  :placeholder="$t('restaurant_item.restaurant_item_name')"
                                                  v-model="editingRestaurantItem.restaurant_item_name"></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="py-0">
                                    <v-text-field outlined
                                                  :label="$t('restaurant_item.restaurant_item_name_tc')"
                                                  :placeholder="$t('restaurant_item.restaurant_item_name_tc')"
                                                  v-model="editingRestaurantItem.restaurant_item_name_tc"></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="py-0">
                                    <v-text-field outlined
                                                  :label="$t('restaurant_item.restaurant_item_name_sc')"
                                                  :placeholder="$t('restaurant_item.restaurant_item_name_sc')"
                                                  v-model="editingRestaurantItem.restaurant_item_name_sc"></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="py-0">
                                    <v-text-field outlined
                                                  :label="$t('restaurant_item.restaurant_item_pin_top')"
                                                  :placeholder="$t('restaurant_item.restaurant_item_pin_top')"
                                                  v-model="editingRestaurantItem.restaurant_item_pin_top"></v-text-field>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="py-0">
                                    <v-text-field outlined
                                                  :label="$t('restaurant_item.restaurant_item_pin_left')"
                                                  :placeholder="$t('restaurant_item.restaurant_item_pin_left')"
                                                  v-model="editingRestaurantItem.restaurant_item_pin_left"></v-text-field>
                                </v-col>
                            </v-row>

                            <v-row>
                                <v-col class="pb-0">{{$t('restaurant_item.html_desc')}}</v-col>
                            </v-row>
                            <v-row>
                                <v-col>
                                    <froala
                                        v-if="showEditor"
                                        :config="froalaConfig"
                                        v-model="editingRestaurantItem.html_desc"
                                    ></froala>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="pb-0">{{$t('restaurant_item.html_desc_tc')}}</v-col>
                            </v-row>
                            <v-row>
                                <v-col>
                                    <froala
                                        v-if="showEditor"
                                        :config="froalaConfig"
                                        v-model="editingRestaurantItem.html_desc_tc"
                                    ></froala>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col class="pb-0">{{$t('restaurant_item.html_desc_sc')}}</v-col>
                            </v-row>
                            <v-row>
                                <v-col>
                                    <froala
                                        v-if="showEditor"
                                        :config="froalaConfig"
                                        v-model="editingRestaurantItem.html_desc_sc"
                                    ></froala>
                                </v-col>
                            </v-row>
                        </v-form>
                    </v-container>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions>
                    <v-btn color="secondary" @click="confirmCancelItemDialogShown = true">{{$t('cancel')}}</v-btn>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" @click="saveRestaurantItem"
                           :disabled="!isEditRestaurantItemFormValid">{{$t('save')}}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
        <PopupAlert 
            v-model="confirmCancelItemDialogShown"
            title="Warning"
            message="Do you wish to cancel your amendments?"
            :onYes="cancelEditAgendaItem"
            :onNo="function() {confirmCancelItemDialogShown=false;}"
            ></PopupAlert>
        <v-overlay :z-index="999" v-model="overlay">
            <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>
    </v-card>
</template>

<style scoped>
    .button-spacer {
        width: 10px;
    }
</style>

<script>
    import {computed, ref, onMounted, watch} from "@vue/composition-api";
    import {useFind} from "feathers-vuex";
    import {DateTime} from "luxon";
    import {cloneDeep} from "lodash";
    import Sortable from "sortablejs";
    import PopupAlert from "../../components/PopupAlert";
    import froalaConfig from "../../froala-config";

    export default {
        name: 'EventRestaurantItemList',
        props: {
            restaurantId: String,
        },
        components:{PopupAlert},
        setup(props, {root}) {
            const {RestaurantItem} = root.$FeathersVuex.api;

            const showEditor = ref(false);

            const restaurantItemParams = computed(() => {
                return {
                    query: {
                        fRestaurantId: props.restaurantId,
                        $sort: {order: 1},
                    }
                }
            })
            const restaurantItemQueryWhen = computed(() => {
                return !!props.restaurantId;
            });
            const {items: restaurantItems, isPending: isRestaurantItemPending} = useFind({
                model: RestaurantItem,
                params: restaurantItemParams,
                queryWhen: restaurantItemQueryWhen,
            });

            // Data Table
            const headers = ref([
                {text:'',value:'scroller',width:80},
                {text: root.$t('restaurant_item.order'), value: 'order'},
                {text: root.$t('restaurant_item.restaurant_item_key'), value: 'restaurant_item_key',  width: '20%'},
                {text: root.$t('restaurant_item.html_desc'), value: 'html_desc', width: '50%'},
                {text: '', value: 'actions'},
            ]);

            const currentStart = ref(0);

            const currentPagination = function(e){
                currentStart.value = e.pageStart
            }
            // Add / Edit Agenda Item
            const editRestaurantItemDialogShown = ref(false);
            const confirmCancelItemDialogShown = ref(false);
            const isEditRestaurantItemFormValid = ref(false);

            const editOrderRestaurant = ref([])
            const didOrderChange = ref(false);
            const isEditOrderMode = ref(false);
            const overlay = ref(false);
            const inlineSaving = ref(false);

            const editingRestaurantItem = ref(new RestaurantItem({fRestaurantId: props.restaurantId}));
            const inlineEditingRestaurantItemList = ref({})
            const initAddRestaurantItem = function () {
                editingRestaurantItem.value = new RestaurantItem({fRestaurantId: props.restaurantId});
                editingRestaurantItem.value.order = restaurantItems.value.length + 1
                editRestaurantItemDialogShown.value = true;
            };
            const initEditRestaurantItem = function (restaurantItem) {
                if(!isEditOrderMode.value){
                    editingRestaurantItem.value = cloneDeep(restaurantItem);
                    editRestaurantItemDialogShown.value = true;
                }
            };
            const deleteRestaurantItem = function (restaurantItem) {
                if(!isEditOrderMode.value){                
                    if (confirm(root.$t('restaurant_item.confirm_delete'))) {
                        restaurantItem.remove().then(() => {
                        }, root.$handleFeatherError.bind(root));
                    }
                }
            };
            const saveRestaurantItem = async function () {
                editingRestaurantItem.value.save().then(() => {
                    RestaurantItem.get(editingRestaurantItem.value.id);
                    editRestaurantItemDialogShown.value = false;
                    root.$store.dispatch('showSnackBar', root.$t('restaurant_item.save_successful'));
                }, root.$handleFeatherError.bind(root));
            };
            const cancelEditAgendaItem = function(){
                editRestaurantItemDialogShown.value = false;
                confirmCancelItemDialogShown.value = false;
            }
            watch(restaurantItems,(restaurantItems,old)=>{
                const newList = {}
                for(let i=0;i<restaurantItems.length;i++){
                    newList[restaurantItems[i].id] = cloneDeep(restaurantItems[i])
                }
                inlineEditingRestaurantItemList.value = newList
            })

            const previewHtmlContentOption = {
                 modules: {
                    toolbar: false,
                },
                placeholder: '',
                theme: 'bubble',
            };
            const openInline = function(restaurantItem){
                inlineEditingRestaurantItemList.value[restaurantItem.id] = cloneDeep(restaurantItem)
            }
            const cancelInline = function(item, ref=null){
                if (ref) {
                    this.$refs[ref].isActive = false;
                }
            }
            const saveInlineItem = async function (item, ref=null){
                inlineSaving.value = true;
                await inlineEditingRestaurantItemList.value[item.id].save().then(()=>{
                    RestaurantItem.get(item.id).then(()=>{
                    });
                    root.$store.dispatch('showSnackBar', root.$t('restaurant_item.save_successful'));
                }, root.$handleFeatherError.bind(root));
                inlineSaving.value = false;
                if (ref) {
                    this.$refs[ref].isActive = false;
                }
            }
            
            const initEditOrder = function() {
                didOrderChange.value = false
                isEditOrderMode.value = true
                editOrderRestaurant.value = cloneDeep(sortedRestaurantItems.value);
            }
            const saveEditOrder = async function(){
                if(!didOrderChange.value){
                    isEditOrderMode.value = false
                    return;
                }

                overlay.value = true
                for(let i=0;i<editOrderRestaurant.value.length;i++){
                    await editOrderRestaurant.value[i].save().then(()=>{
                        RestaurantItem.get(editOrderRestaurant.value[i].id)
                    })
                }
                isEditOrderMode.value = false
                overlay.value = false;
            }

            const cancelEditOrder = function(){
                isEditOrderMode.value = false;
            }

            const quickSortRestaurantItems = function(sortRestaurantItems){
                if(sortRestaurantItems.length<2){
                    return sortRestaurantItems
                }

                const chosenIndex = sortRestaurantItems.length - 1
                const chosen  = sortRestaurantItems[chosenIndex]
                const a = []
                const b = []
                for(let i=0;i<chosenIndex;i++){
                    const temp = sortRestaurantItems[i].order
                    temp < chosen.order ? a.push(sortRestaurantItems[i]) : b.push(sortRestaurantItems[i])
                }
                const output = [...quickSortRestaurantItems(a),chosen,...quickSortRestaurantItems(b)]
                return output
            }

            const tableRestaurants = computed(()=>{
                return isEditOrderMode.value ? editOrderRestaurant.value : sortedRestaurantItems.value;
            })

            const sortedRestaurantItems = computed(()=>{
                return quickSortRestaurantItems(restaurantItems.value)
            })

            onMounted(() => {
                let tables = document.querySelectorAll('.v-data-table tbody');
                Sortable.create(tables[1],
                    {
                        handle:".handle", // Look for class with name "handle"
                        onEnd({newIndex,oldIndex}){
                            newIndex +=  currentStart.value;
                            oldIndex +=  currentStart.value;
                            const rowSelected = editOrderRestaurant.value.splice(oldIndex,1)[0];
                            editOrderRestaurant.value.splice(newIndex,0,rowSelected);

                            didOrderChange.value = true
                            for(let i=0;i<editOrderRestaurant.value.length;i++){
                                editOrderRestaurant.value[i].order = i+1
                            }
                            editOrderRestaurant.value = quickSortRestaurantItems(editOrderRestaurant.value);
                        }
                    }
                )
            });

            const restaurantOrderRule = function(value){
                const onlyNumberRule =  /^[0-9]+$/;
                return onlyNumberRule.test(value) || 'Invalid Number';
            }

            return {
                restaurantItems,
                tableRestaurants,
                isRestaurantItemPending,
                inlineEditingRestaurantItemList,
                headers,

                editRestaurantItemDialogShown,
                confirmCancelItemDialogShown,
                initAddRestaurantItem,
                initEditRestaurantItem,
                deleteRestaurantItem,
                isEditRestaurantItemFormValid,
                editingRestaurantItem,
                saveRestaurantItem,
                cancelEditAgendaItem,
                
                DateTime,

                previewHtmlContentOption,
                openInline,
                cancelInline,
                saveInlineItem,

                overlay,
                inlineSaving,
                isEditOrderMode,
                initEditOrder,
                saveEditOrder,
                cancelEditOrder,

                currentPagination,
                restaurantOrderRule,

                showEditor,
                froalaConfig: {
                    ...froalaConfig,
                    toolbarSticky: false,
                    zIndex: 2000
                },
            }
        },
        mounted() {
            setTimeout(() => {
               this.showEditor = true;
            }, 100);
        }
    }
</script>
